import { useState, useRef, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import './ConversionLanding.css';

export default function ConversionLanding() {
  const [childName, setChildName] = useState('');
  const [age, setAge] = useState('5');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [story, setStory] = useState(null);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [optInMarketing, setOptInMarketing] = useState(false);
  const storyRef = useRef(null);
  const formRef = useRef(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToStory = () => {
    storyRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const generateStory = async (e) => {
    e.preventDefault();
    
    if (!childName.trim()) {
      toast.error('Bitte gib den Namen deines Kindes ein');
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setGenerationProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 500);

    try {
      const response = await axios.post(`${API}/guest/create-story`, {
        child_name: childName.trim(),
        age: parseInt(age),
        language: 'de'
      });

      clearInterval(progressInterval);
      setGenerationProgress(100);
      
      setStory(response.data);
      
      // Scroll to story after a brief delay
      setTimeout(() => {
        scrollToStory();
      }, 300);

    } catch (error) {
      console.error('Story generation error:', error);
      toast.error('Fehler beim Erstellen der Geschichte. Bitte versuche es erneut.');
    } finally {
      clearInterval(progressInterval);
      setIsGenerating(false);
    }
  };

  const sendStoryByEmail = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !email.includes('@')) {
      toast.error('Bitte gib eine gültige E-Mail-Adresse ein');
      return;
    }

    setSendingEmail(true);

    try {
      await axios.post(`${API}/guest/send-story-email`, {
        email: email.trim(),
        story_id: story.id,
        child_name: childName,
        opt_in_marketing: optInMarketing
      });

      setEmailSent(true);
      toast.success('Geschichte wurde gesendet! Schau in dein Postfach.');

    } catch (error) {
      console.error('Email send error:', error);
      toast.error('Fehler beim Senden. Bitte versuche es erneut.');
    } finally {
      setSendingEmail(false);
    }
  };

  const createAnotherStory = () => {
    setStory(null);
    setChildName('');
    setEmail('');
    setEmailSent(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="conversion-landing">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-headline">
            Eine persönliche Gute-Nacht-Geschichte für dein Kind – heute Abend.
          </h1>
          <p className="hero-subheadline">
            Individuelle Geschichten zum Vorlesen mit dem Namen deines Kindes.
            <br />In unter 60 Sekunden erstellt und sofort lesbar.
          </p>
          <p className="trust-line">
            ✓ Kostenlos &nbsp; ✓ Kein Abo &nbsp; ✓ Kein Login
          </p>
          <Button 
            className="hero-cta-button"
            onClick={scrollToForm}
            size="lg"
          >
            📖 Jetzt kostenlose Geschichte lesen
          </Button>
          <p className="micro-text">
            Perfekt für das Einschlaf-Ritual heute Abend
          </p>
        </div>
        <div className="hero-image-container">
          <div className="hero-image-placeholder">
            <span className="hero-emoji">🌙</span>
          </div>
        </div>
      </section>

      {/* Story Creation Form */}
      <section className="form-section" ref={formRef}>
        <div className="form-container">
          <form onSubmit={generateStory}>
            <div className="form-field">
              <label htmlFor="childName">✨ Wie heißt dein Kind?</label>
              <Input
                id="childName"
                type="text"
                placeholder="z. B. Emma"
                value={childName}
                onChange={(e) => setChildName(e.target.value)}
                disabled={isGenerating}
                className="name-input"
              />
            </div>
            
            <div className="form-field">
              <label htmlFor="age">Alter</label>
              <Select value={age} onValueChange={setAge} disabled={isGenerating}>
                <SelectTrigger className="age-select">
                  <SelectValue placeholder="Alter wählen" />
                </SelectTrigger>
                <SelectContent>
                  {[3, 4, 5, 6, 7, 8].map(a => (
                    <SelectItem key={a} value={String(a)}>{a} Jahre</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button 
              type="submit" 
              className="generate-button"
              disabled={isGenerating || !childName.trim()}
              size="lg"
            >
              {isGenerating ? (
                <span className="generating-text">
                  <span className="spinner">✨</span>
                  Geschichte wird erstellt...
                </span>
              ) : (
                `✨ Geschichte für ${childName || 'dein Kind'} erstellen`
              )}
            </Button>

            {isGenerating && (
              <div className="progress-container">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${generationProgress}%` }}
                  />
                </div>
                <p className="progress-text">
                  {generationProgress < 30 && 'Die Geschichte wird geschrieben...'}
                  {generationProgress >= 30 && generationProgress < 60 && 'Die Magie entfaltet sich...'}
                  {generationProgress >= 60 && generationProgress < 90 && 'Fast fertig...'}
                  {generationProgress >= 90 && 'Gleich bereit zum Vorlesen!'}
                </p>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Story Display Section */}
      {story && (
        <section className="story-section" ref={storyRef}>
          <div className="story-container">
            <h2 className="story-title">{story.title}</h2>
            
            <div className="story-pages">
              {story.pages?.map((page, index) => (
                <div key={index} className="story-page">
                  {page.image_url && (
                    <img 
                      src={page.image_url} 
                      alt={`Seite ${index + 1}`}
                      className="story-image"
                    />
                  )}
                  <p className="story-text">{page.text}</p>
                </div>
              ))}
            </div>

            <div className="story-end">
              <span className="end-emoji">🌙</span>
              <p>Ende</p>
            </div>

            {/* Email Handover - After Story */}
            <div className="email-handover">
              {!emailSent ? (
                <>
                  <h3>Möchtest du diese Geschichte später nochmal lesen?</h3>
                  <p className="email-description">
                    Ich schicke dir die Geschichte per E-Mail,<br />
                    damit du sie heute Abend oder morgen wieder öffnen kannst.
                  </p>
                  <form onSubmit={sendStoryByEmail} className="email-form">
                    <Input
                      type="email"
                      placeholder="Deine E-Mail-Adresse"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={sendingEmail}
                      className="email-input"
                    />
                    <label className="opt-in-checkbox">
                      <input
                        type="checkbox"
                        checked={optInMarketing}
                        onChange={(e) => setOptInMarketing(e.target.checked)}
                        disabled={sendingEmail}
                      />
                      <span>Ja, ich möchte auch Tipps für schöne Vorlesemomente erhalten</span>
                    </label>
                    <Button 
                      type="submit" 
                      className="email-button"
                      disabled={sendingEmail || !email.trim()}
                    >
                      {sendingEmail ? 'Wird gesendet...' : '📩 Geschichte per E-Mail erhalten'}
                    </Button>
                  </form>
                  <p className="email-note">
                    Kein Spam. Kein Abo. Nur deine Geschichte.
                  </p>
                </>
              ) : (
                <div className="email-success">
                  <span className="success-emoji">✉️</span>
                  <h3>Geschichte gesendet!</h3>
                  <p>Schau in dein E-Mail-Postfach.</p>
                  
                  {/* Optional Registration */}
                  <div className="optional-register">
                    <p>
                      Wenn du möchtest, kannst du ein kostenloses Eltern-Profil anlegen<br />
                      und alle Geschichten jederzeit wiederfinden.
                    </p>
                    <a href="https://storytales.xyz/auth" className="register-link">
                      Optional registrieren →
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      <section className="benefits-section">
        <h2>Warum Eltern Storytales lieben</h2>
        <div className="benefits-grid">
          <div className="benefit-card">
            <span className="benefit-emoji">🧒</span>
            <h3>Dein Kind im Mittelpunkt</h3>
            <p>Der Name und das Alter deines Kindes machen die Geschichte besonders persönlich.</p>
          </div>
          <div className="benefit-card">
            <span className="benefit-emoji">📖</span>
            <h3>Perfekt zum Vorlesen</h3>
            <p>Ruhige, kindgerechte Texte – ideal als festes Abendritual.</p>
          </div>
          <div className="benefit-card">
            <span className="benefit-emoji">😴</span>
            <h3>Entspannter einschlafen</h3>
            <p>Weniger Diskussionen, mehr Vorfreude auf die Schlafenszeit.</p>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="testimonials-section">
        <h2>Das sagen andere Eltern</h2>
        <div className="testimonials-grid">
          <div className="testimonial-card">
            <div className="stars">⭐⭐⭐⭐⭐</div>
            <p className="testimonial-text">
              "Unser Sohn hört viel aufmerksamer zu, wenn er selbst Teil der Geschichte ist."
            </p>
            <p className="testimonial-author">— Mama von Leon (6)</p>
          </div>
          <div className="testimonial-card">
            <div className="stars">⭐⭐⭐⭐⭐</div>
            <p className="testimonial-text">
              "Endlich eine ruhige Einschlafroutine ohne Stress."
            </p>
            <p className="testimonial-author">— Papa von Emma (7)</p>
          </div>
        </div>
      </section>

      {/* Relatable Moment */}
      <section className="relatable-section">
        <h2>Kennst du diese Abende?</h2>
        <p className="relatable-text">
          Dein Kind ist müde, aber kommt nicht zur Ruhe.<br />
          Noch eine Frage. Noch ein Wunsch. Noch eine Diskussion.
        </p>
        <p className="relatable-solution">
          Storytales macht daraus<br />
          <strong>einen ruhigen Moment zum Vorlesen und Einschlafen.</strong>
        </p>
      </section>

      {/* Secondary CTA */}
      <section className="secondary-cta-section">
        <Button 
          className="secondary-cta-button"
          onClick={story ? createAnotherStory : scrollToForm}
          size="lg"
        >
          🌙 {story ? 'Noch eine Geschichte erstellen' : 'Jetzt Geschichte erstellen'}
        </Button>
        <p className="secondary-cta-subtext">Kostenlos · Sofort lesbar</p>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-links">
          <a href="https://storytales.xyz/impressum">Impressum</a>
          <span className="footer-divider">·</span>
          <a href="https://storytales.xyz/datenschutz">Datenschutz</a>
          <span className="footer-divider">·</span>
          <a href="https://storytales.xyz/kontakt">Kontakt</a>
        </div>
        <p className="footer-brand">
          <a href="https://storytales.xyz">storytales.xyz</a>
        </p>
      </footer>
    </div>
  );
}
